import { useCallback, useMemo } from "react";
import type { DateRange } from "react-day-picker";

import { Prisma, type ListingStatus } from "@/.zenstack/logical-prisma-client";
import { useFindManyListing } from "@/lib/hooks/generated/tanstack-query";

// TODO: Make the response model typed based on a generic, so the
// response model can be tailored to the use case.
// e.g. useListing<T>(...): { data: T[] }

// TODO: Make a post-query predicate[] on the input type to create
// summaries of the data.
// e.g. useListing<T>(predicates: Predicates[], ...): { data: T[], predicateResults: {Predicate, Result}[] }

// Define the select/include structure as a reusable constant
export const LISTING_DETAILED_SELECT = {
  business: true, // Include basic business info
  files: true,
  atAuction: {
    include: {
      bids: {
        include: {
          propertySeeker: {
            include: {
              user: true,
            },
          },
        },
        orderBy: [
          { currentBidAmount: Prisma.SortOrder.desc },
          { createdAt: Prisma.SortOrder.desc },
        ],
      },
      winningBid: {
        include: {
          propertySeeker: {
            include: {
              user: true,
            },
          },
        },
      },
    },
  },
  forLease: { include: { tenantScreeningCriteria: true } },
  comments: {
    include: {
      user: {
        select: {
          id: true,
        },
      },
      replies: {
        include: {
          user: {
            select: {
              id: true,
            },
          },
        },
      },
    },
  },
};

// Extract the TypeScript type from the select/include structure
export type ListingDetailedType = Prisma.ListingGetPayload<{
  include: typeof LISTING_DETAILED_SELECT;
}>;

type QuickFilterType =
  | "all"
  | "leading"
  | "outbid"
  | "DRAFT"
  | "PENDING"
  | "ACTIVE";

interface UseListingsOptions {
  businessId?: string;
  status?: ListingStatus | ListingStatus[] | "all" | null | undefined;
  dateRange?: DateRange;
  search?: string;
  savedByUserId?: string;
  biddedByUserId?: string;
  commentedByUserId?: string;
  pageSize?: number;
  page?: number;
  // New bid filtering options
  bidTimeFilter?: "current" | "past";
  bidStatusFilter?: "all" | "leading" | "outbid";
  // New time filtering option for general listings
  timeFilter?: "current" | "past";
  // Consolidated options for all filtering scenarios
  quickFilter?: QuickFilterType;
  includeTabCounts?: boolean;
  savedListingIds?: Set<string>;
  userId?: string; // For bid status calculations
}

interface UseListingsReturn {
  // Core data (backward compatible)
  data: ListingDetailedType[];
  isLoading: boolean;
  error: unknown;

  // Tab-based data (when timeFilter is not specified)
  currentListings: ListingDetailedType[];
  pastListings: ListingDetailedType[];

  // Tab counts (when includeTabCounts is true)
  totalCurrentCount: number;
  totalPastCount: number;

  // Quick filtered data (when quickFilter is specified)
  filteredCurrentListings: ListingDetailedType[];
  filteredPastListings: ListingDetailedType[];
}

export function useListings({
  businessId,
  status = "all",
  dateRange,
  search,
  savedByUserId,
  biddedByUserId,
  commentedByUserId,
  pageSize = 25,
  page = 1,
  bidTimeFilter,
  bidStatusFilter,
  timeFilter,
  quickFilter,
  includeTabCounts = false,
  savedListingIds,
  userId,
}: UseListingsOptions = {}): UseListingsReturn {
  const skip = (page - 1) * pageSize;

  const fromTimestamp = dateRange?.from?.getTime?.();
  const toTimestamp = dateRange?.to?.getTime?.();

  // Helper function to parse auction end date
  const parseAuctionEnd = useCallback((value: unknown): Date | undefined => {
    if (value == null) return undefined;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? undefined : value;
    }
    if (typeof value === "string" || typeof value === "number") {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? undefined : parsed;
    }
    return undefined;
  }, []);

  // Helper function to apply quick filters
  const applyQuickFilter = useCallback(
    (listings: ListingDetailedType[]) => {
      if (!quickFilter || quickFilter === "all") return listings;

      return listings.filter((l) => {
        if (quickFilter === "DRAFT") {
          return l.status === "DRAFT";
        } else if (quickFilter === "PENDING") {
          return l.status === "PENDING";
        } else if (quickFilter === "ACTIVE") {
          return l.status === "ACTIVE";
        } else if (quickFilter === "leading") {
          // User is leading if they have the highest bid
          const highestBid = l.atAuction?.bids?.[0];
          return highestBid && highestBid.propertySeekerId === userId;
        } else if (quickFilter === "outbid") {
          // User is outbid if they have at least one bid but are not leading
          const hasUserBid = l.atAuction?.bids?.some(
            (b) => b.propertySeekerId === userId,
          );
          const highestBid = l.atAuction?.bids?.[0];
          const isLeading =
            highestBid && highestBid.propertySeekerId === userId;
          return hasUserBid && !isLeading;
        }
        return true;
      });
    },
    [quickFilter, userId],
  );

  // Helper function to apply time-based filtering for favorites
  const applyTimeFilter = useCallback(
    (listings: ListingDetailedType[], timeType: "current" | "past") => {
      if (!savedListingIds) return listings;

      // Filter to only saved listings
      const filtered = listings.filter((l) => savedListingIds.has(l.id));

      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      return filtered.filter((l) => {
        if (!l.atAuction) return false;

        const auctionEndDate = new Date(l.atAuction.auctionEndDate);

        if (timeType === "current") {
          // Current: auction hasn't ended yet
          if (auctionEndDate > now) {
            return true;
          }

          // Current: auction ended less than a week ago and user won
          if (auctionEndDate > oneWeekAgo) {
            const winningBid = l.atAuction?.winningBid;
            return winningBid?.propertySeekerId === userId;
          }
        } else {
          // Past: auction ended more than a week ago
          if (auctionEndDate <= oneWeekAgo) {
            return true;
          }

          // Past: auction ended less than a week ago but user didn't win
          if (auctionEndDate <= now && auctionEndDate > oneWeekAgo) {
            const winningBid = l.atAuction?.winningBid;
            return winningBid?.propertySeekerId !== userId; // Show in past if user didn't win
          }
        }

        return false;
      });
    },
    [savedListingIds, userId],
  );

  // Memoize the query args to stabilize referential identity between renders
  const queryArgs = useMemo(() => {
    const where: Prisma.ListingWhereInput = {};

    if (businessId) {
      where.businessId = businessId;
    }

    if (status && status !== "all") {
      if (Array.isArray(status)) {
        where.status = { in: status };
      } else {
        where.status = status;
      }
    }

    if (savedByUserId) {
      where.savedListings = {
        some: {
          personId: savedByUserId,
          isSaved: true,
        },
      };
    }

    // Initialize atAuction conditions
    const atAuctionConditions: Prisma.AuctionFormatWhereInput = {};

    if (fromTimestamp || toTimestamp) {
      atAuctionConditions.AND = {
        ...(fromTimestamp
          ? { auctionStartDate: { gte: new Date(fromTimestamp) } }
          : {}),
        ...(toTimestamp
          ? { auctionEndDate: { lte: new Date(toTimestamp) } }
          : {}),
      };
    }

    // Bid time filtering based on auction end date
    if (bidTimeFilter) {
      const now = new Date();
      if (bidTimeFilter === "current") {
        atAuctionConditions.AND = {
          ...atAuctionConditions.AND,
          auctionEndDate: { gt: now },
        };
      } else if (bidTimeFilter === "past") {
        atAuctionConditions.AND = {
          ...atAuctionConditions.AND,
          auctionEndDate: { lte: now },
        };
      }
    }

    // General time filtering for listings (current/past auctions)
    if (timeFilter) {
      if (timeFilter === "current") {
        // Include listings that haven't happened yet or are currently happening
        // This includes: DRAFT, PENDING, ACTIVE, PAUSED, STOPPED
        // Excludes: CLOSED, ARCHIVED
        const currentStatuses: ListingStatus[] = [
          "DRAFT",
          "PENDING",
          "ACTIVE",
          "PAUSED",
          "STOPPED",
        ];

        // If a specific status is requested, intersect it with current statuses
        if (status && status !== "all") {
          if (Array.isArray(status)) {
            where.status = {
              in: status.filter((s) => currentStatuses.includes(s)),
            };
          } else {
            where.status = currentStatuses.includes(status)
              ? status
              : { in: [] };
          }
        } else {
          where.status = { in: currentStatuses };
        }
      } else if (timeFilter === "past") {
        // Include listings that are definitively finished
        // This includes: CLOSED, ARCHIVED
        const pastStatuses: ListingStatus[] = ["CLOSED", "ARCHIVED"];

        // If a specific status is requested, intersect it with past statuses
        if (status && status !== "all") {
          if (Array.isArray(status)) {
            where.status = {
              in: status.filter((s) => pastStatuses.includes(s)),
            };
          } else {
            where.status = pastStatuses.includes(status) ? status : { in: [] };
          }
        } else {
          where.status = { in: pastStatuses };
        }
      }
    } else {
      // No time filter, apply status filter directly
      if (status && status !== "all") {
        if (Array.isArray(status)) {
          where.status = { in: status };
        } else {
          where.status = status;
        }
      }
    }

    // Bid status filtering (secondary filter)
    if (bidStatusFilter && biddedByUserId) {
      if (bidStatusFilter === "leading") {
        // User is leading if they have the highest bid amount
        atAuctionConditions.AND = {
          ...atAuctionConditions.AND,
          bids: {
            some: {
              propertySeekerId: biddedByUserId,
              // This will be handled in the include to get all bids for comparison
            },
          },
        };
      } else if (bidStatusFilter === "outbid") {
        // User is outbid if they have at least one bid but are not leading
        // This requires complex logic that's better handled client-side after fetching
        atAuctionConditions.AND = {
          ...atAuctionConditions.AND,
          bids: {
            some: {
              propertySeekerId: biddedByUserId,
            },
          },
        };
      }
    }

    if (biddedByUserId) {
      atAuctionConditions.AND = {
        ...atAuctionConditions.AND,
        bids: {
          some: {
            propertySeekerId: biddedByUserId,
          },
        },
      };
    }

    // Only set atAuction if we have conditions
    if (Object.keys(atAuctionConditions).length > 0) {
      where.atAuction = atAuctionConditions;
    }

    if (search) {
      where.OR = [
        {
          title: {
            contains: search,
            mode: "insensitive" as const,
          },
        },
        {
          details: {
            path: ["address.streetLine1", "address.city", "address.state"],
            string_contains: search,
          },
        },
      ];
    }

    if (commentedByUserId) {
      where.OR = [
        ...(where.OR || []),
        {
          comments: {
            some: {
              userId: commentedByUserId,
            },
          },
        },
        {
          comments: {
            some: {
              replies: {
                some: {
                  userId: commentedByUserId,
                },
              },
            },
          },
        },
      ];
    }

    // Create the include object with conditional bid filtering
    const includeWithConditionalBids = {
      ...LISTING_DETAILED_SELECT,
      atAuction: {
        include: {
          ...LISTING_DETAILED_SELECT.atAuction.include,
          bids: {
            ...LISTING_DETAILED_SELECT.atAuction.include.bids,
            // When filtering by saved listings or bid status, include all bids to check user's bid status
            ...(savedByUserId || bidStatusFilter ? {} : { take: 1 }),
          },
        },
      },
    };

    return {
      where,
      include: includeWithConditionalBids,
      orderBy: [
        {
          atAuction: {
            auctionEndDate: Prisma.SortOrder.desc,
          },
        },
      ],
      skip,
      take: pageSize,
    };
  }, [
    businessId,
    status,
    savedByUserId,
    biddedByUserId,
    commentedByUserId,
    skip,
    pageSize,
    fromTimestamp,
    toTimestamp,
    search,
    bidTimeFilter,
    bidStatusFilter,
    timeFilter,
  ]);

  // Main query for filtered data
  const mainQuery = useFindManyListing(queryArgs);

  // For tab counts, we need unfiltered data (no search, no quick filter)
  // but with the same base filters (businessId, savedByUserId, etc.)
  const needsTabCounts = includeTabCounts && !timeFilter;

  const unfilteredQueryArgs = useMemo(() => {
    if (!needsTabCounts) {
      return {
        where: { id: { in: [] } },
        include: queryArgs.include,
        orderBy: queryArgs.orderBy,
        skip: 0,
        take: 0,
      }; // Empty query when not needed
    }

    // Create base query args without search and quick filter
    const baseArgs = { ...queryArgs };

    // Remove search from where clause for counters
    if (baseArgs.where.OR) {
      baseArgs.where = { ...baseArgs.where, OR: undefined };
    }

    // Remove status filtering for tab counts when using quick filter
    if (quickFilter && quickFilter !== "all") {
      baseArgs.where = { ...baseArgs.where, status: undefined };
    }

    return baseArgs;
  }, [needsTabCounts, queryArgs, quickFilter]);

  const unfilteredQuery = useFindManyListing(
    needsTabCounts ? unfilteredQueryArgs : undefined,
  );

  // Process the main data
  const mainData = useMemo(() => mainQuery.data || [], [mainQuery.data]);

  // Split into current/past if not using timeFilter
  const currentListings = useMemo(() => {
    if (timeFilter === "current") return mainData;
    if (timeFilter === "past") return [];

    const now = new Date();
    return mainData.filter((l) => {
      const end = parseAuctionEnd(l.atAuction?.auctionEndDate);
      return end ? end > now : true;
    });
  }, [mainData, timeFilter, parseAuctionEnd]);

  const pastListings = useMemo(() => {
    if (timeFilter === "past") return mainData;
    if (timeFilter === "current") return [];

    const now = new Date();
    return mainData.filter((l) => {
      const end = parseAuctionEnd(l.atAuction?.auctionEndDate);
      return end ? end <= now : false;
    });
  }, [mainData, timeFilter, parseAuctionEnd]);

  // Apply quick filters
  const filteredCurrentListings = useMemo(() => {
    const baseListings = currentListings;

    // Apply time filter for favorites
    const timeFiltered = savedListingIds
      ? applyTimeFilter(baseListings, "current")
      : baseListings;

    // Apply quick filter
    return applyQuickFilter(timeFiltered);
  }, [currentListings, applyTimeFilter, applyQuickFilter, savedListingIds]);

  const filteredPastListings = useMemo(() => {
    const baseListings = pastListings;

    // Apply time filter for favorites
    const timeFiltered = savedListingIds
      ? applyTimeFilter(baseListings, "past")
      : baseListings;

    // Apply quick filter
    return applyQuickFilter(timeFiltered);
  }, [pastListings, applyTimeFilter, applyQuickFilter, savedListingIds]);

  // Calculate tab counts from unfiltered data
  const totalCurrentCount = useMemo(() => {
    if (!needsTabCounts) return 0;

    const unfilteredData = unfilteredQuery.data || [];

    // Apply the same time-based splitting logic as the main data
    const now = new Date();
    const currentItems = unfilteredData.filter((l) => {
      const end = parseAuctionEnd(l.atAuction?.auctionEndDate);
      return end ? end > now : true;
    });

    // Apply time filter for favorites if needed
    const timeFiltered = savedListingIds
      ? applyTimeFilter(currentItems, "current")
      : currentItems;

    return timeFiltered.length;
  }, [
    needsTabCounts,
    unfilteredQuery.data,
    parseAuctionEnd,
    savedListingIds,
    applyTimeFilter,
  ]);

  const totalPastCount = useMemo(() => {
    if (!needsTabCounts) return 0;

    const unfilteredData = unfilteredQuery.data || [];

    // Apply the same time-based splitting logic as the main data
    const now = new Date();
    const pastItems = unfilteredData.filter((l) => {
      const end = parseAuctionEnd(l.atAuction?.auctionEndDate);
      return end ? end <= now : false;
    });

    // Apply time filter for favorites if needed
    const timeFiltered = savedListingIds
      ? applyTimeFilter(pastItems, "past")
      : pastItems;

    return timeFiltered.length;
  }, [
    needsTabCounts,
    unfilteredQuery.data,
    parseAuctionEnd,
    savedListingIds,
    applyTimeFilter,
  ]);

  return {
    // Core data (backward compatible)
    data: mainData,
    isLoading: mainQuery.isLoading,
    error: mainQuery.error,

    // Tab-based data
    currentListings,
    pastListings,

    // Tab counts
    totalCurrentCount,
    totalPastCount,

    // Quick filtered data
    filteredCurrentListings,
    filteredPastListings,
  };
}
